import React, { useEffect, useState, useCallback } from "react";
import { api, PLATFORM_LABELS } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CaseDetail } from "@/components/CaseDetail";
import { Search, ExternalLink, Trash2, ShieldAlert, Newspaper } from "lucide-react";
import { toast } from "sonner";

export default function Medien() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = useCallback(async () => {
    const params = { category: "erwaehnung" };
    if (q) params.q = q;
    const r = await api.get("/cases", { params });
    setItems(r.data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (id) => {
    await api.delete(`/cases/${id}`);
    toast.success("Erwähnung entfernt.");
    load();
  };

  const promote = async (id) => {
    await api.patch(`/cases/${id}/category`, { category: "verstoss" });
    toast.success("Als Verstoß in die Fallakte verschoben.");
    load();
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");

  const ORIGIN_LABELS = { newsapi: "NewsAPI", google_news: "Google News" };
  const langLabel = (l) => {
    if (!l) return "";
    const s = l.toLowerCase();
    if (s.startsWith("de")) return "DE";
    if (s.startsWith("en")) return "EN";
    return l.toUpperCase().slice(0, 2);
  };
  const srcDate = (c) => {
    const raw = c.source_date || "";
    if (!raw) return "";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d.toLocaleDateString("de-DE");
  };

  const FILTERS = [
    { key: "all", label: "Alle" },
    { key: "newsapi", label: "NewsAPI" },
    { key: "google_news", label: "Google News" },
  ];
  const counts = {
    all: items.length,
    newsapi: items.filter((c) => c.origin === "newsapi").length,
    google_news: items.filter((c) => c.origin === "google_news").length,
  };
  const visibleItems = sourceFilter === "all" ? items : items.filter((c) => c.origin === sourceFilter);

  return (
    <div className="space-y-6" data-testid="medien-page">
      <PageHeader
        title="Medien-Monitoring"
        subtitle="Alle neutralen Erwähnungen eurer Marken im Netz (Presse, Blogs, Foren) – ohne Verstoß, aber gut zu wissen."
      />

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-4 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A6A4A9]" />
          <Input
            data-testid="medien-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Titel oder Keyword suchen …"
            className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] pl-10 focus-visible:ring-[#0A0A0A]"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="medien-source-filter">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`source-filter-${f.key}`}
              onClick={() => setSourceFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                sourceFilter === f.key
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                  : "border-[#E8E4DF] bg-white text-[#6B6A6E] hover:bg-[#FAF7F2]"
              }`}
            >
              {f.label}
              <span className={`rounded-full px-1.5 text-xs ${sourceFilter === f.key ? "bg-white/20" : "bg-[#F4F1EC] text-[#8B8A8E]"}`}>
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden rounded-lg border-[#E8E4DF] bg-white shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <Table>
          <TableHeader>
            <TableRow className="border-[#EDE9E3] hover:bg-transparent">
              <TableHead className="text-[#8B8A8E]">Titel / Quelle</TableHead>
              <TableHead className="text-[#8B8A8E]">Plattform</TableHead>
              <TableHead className="text-[#8B8A8E]">Keyword</TableHead>
              <TableHead className="text-[#8B8A8E]">Gefunden</TableHead>
              <TableHead className="text-right text-[#8B8A8E]">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center text-sm text-[#8B8A8E]">
                  <Newspaper className="mx-auto mb-2 h-6 w-6 text-[#C9C6CB]" />
                  Noch keine Erwähnungen erfasst.
                </TableCell>
              </TableRow>
            )}
            {visibleItems.map((c) => (
              <TableRow key={c.id} data-testid={`mention-row-${c.id}`} className="border-[#EDE9E3]">
                <TableCell
                  className="max-w-[380px] cursor-pointer font-medium text-[#0A0A0A]"
                  onClick={() => setSelected(c)}
                >
                  <span className="line-clamp-2">{c.profile_name}</span>
                  {(c.origin || c.source_name || c.source_date || c.source_lang) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`mention-source-${c.id}`}>
                      {c.origin && (
                        <span className="inline-flex items-center rounded-full bg-[#0A0A0A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          {ORIGIN_LABELS[c.origin] || c.origin}
                        </span>
                      )}
                      {c.source_name && (
                        <span className="inline-flex items-center rounded-full border border-[#E8E4DF] bg-[#FAF7F2] px-2 py-0.5 text-[11px] font-medium text-[#6B6A6E]">
                          {c.source_name}
                        </span>
                      )}
                      {srcDate(c) && (
                        <span className="inline-flex items-center rounded-full border border-[#E8E4DF] bg-[#FAF7F2] px-2 py-0.5 text-[11px] text-[#8B8A8E]">
                          {srcDate(c)}
                        </span>
                      )}
                      {langLabel(c.source_lang) && (
                        <span className="inline-flex items-center rounded-full border border-[#D6CFE8] bg-[#F3F0FA] px-2 py-0.5 text-[10px] font-bold text-[#5B4A8A]">
                          {langLabel(c.source_lang)}
                        </span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#6B6A6E]">{PLATFORM_LABELS[c.platform] || c.platform}</TableCell>
                <TableCell className="text-sm text-[#6B6A6E]">{c.matched_keyword || "—"}</TableCell>
                <TableCell className="text-sm text-[#6B6A6E]">{fmt(c.created_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {c.profile_url && (
                      <a href={c.profile_url} target="_blank" rel="noreferrer" data-testid={`open-link-${c.id}`}
                        className="flex h-9 w-9 items-center justify-center rounded-md text-[#A6A4A9] hover:bg-[#FAF7F2] hover:text-[#0A0A0A]">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <Button
                      data-testid={`promote-${c.id}`}
                      variant="ghost" size="icon"
                      title="Als Verstoß in die Fallakte verschieben"
                      className="h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-amber-50 hover:text-amber-700"
                      onClick={() => promote(c.id)}
                    >
                      <ShieldAlert className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`delete-mention-${c.id}`}
                      variant="ghost" size="icon"
                      className="h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600"
                      onClick={() => remove(c.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <CaseDetail caseItem={selected} onClose={() => setSelected(null)} onUpdated={load} />
    </div>
  );
}
